import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";

export default function PaymentSuccess() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [stripeSessionId, setStripeSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("stripe_session_id");
    if (id) {
      setStripeSessionId(id);
    } else {
      setError("決済情報が見つかりません。");
    }
  }, []);

  const verify = trpc.payments.verifyAndRegister.useMutation({
    onSuccess: (data) => {
      setTimeout(() => setLocation("/my-page"), 2000);
    },
    onError: (e) => {
      setError(e.message || "申し込み登録に失敗しました。");
    },
  });

  useEffect(() => {
    if (stripeSessionId && user && !verify.isPending && !verify.isSuccess && !verify.isError) {
      verify.mutate({ stripeSessionId });
    }
  }, [stripeSessionId, user]);

  if (authLoading || (stripeSessionId && verify.isPending)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-bg">
        <Card className="max-w-md w-full">
          <CardContent className="pt-10 pb-10 flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="text-muted-foreground">決済を確認しています...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-bg">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-2" />
            <CardTitle>エラーが発生しました</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button asChild>
              <Link href="/register">申し込みページに戻る</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link href="/my-page">マイページ</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verify.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-bg">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-2" />
            <CardTitle>お支払いが完了しました！</CardTitle>
            <CardDescription>受験申し込みが正常に登録されました。まもなくマイページに移動します。</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" asChild>
              <Link href="/my-page">マイページへ</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
